/**
 * Roadmap — Grid View
 *
 * Renders Roadmap nodes on a 2D grid: swimlane rows × milestone columns.
 * Features:
 *  - Multiple ordered nodes per cell, each in its own sub-row
 *  - Drag-and-drop with DragOverlay (clean ghost) + source placeholder
 *  - SortableContext within each cell for position-aware insertion
 *  - Cross-cell moves detected via DroppableCell + onDragOver
 *  - In-cell "+" button to add a node directly into a specific cell
 *  - Zoom controls (40% – 150%)
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Plus,
  Loader2,
  Trophy,
  Calendar,
  Target,
  Link2,
  GitMerge,
  ArrowDownToLine,
  GripHorizontal,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  Info,
} from 'lucide-react';
import type {
  RoadmapNode,
  RoadmapSwimlane,
  RoadmapMilestoneColumn,
  RoadmapNodeStatus,
  RoadmapCellRelationship,
} from '../../stores/prioritization/roadmapStore';
import { useRoadmapStore } from '../../stores/prioritization/roadmapStore';
import { PlanNodeWorkspaceItemBadge } from '../planning/PlanNodeWorkspaceItemBadge';
import { isPlanNodeAwaitingFirstSave } from '../../lib/planNodeDraft';
import { useVisualViewportFlags } from '../../hooks/prioritization/useVisualViewport';

function graphVisible(nodes: RoadmapNode[]): RoadmapNode[] {
  return nodes.filter((n) => !isPlanNodeAwaitingFirstSave(n.text));
}

/** Order when clicking the cell connector chip (semantic → cosmetic divider). */
const CELL_REL_CYCLE: RoadmapCellRelationship[] = ['sequential', 'parallel', 'then', 'divider'];

function advanceCellRelationship(current: RoadmapCellRelationship): RoadmapCellRelationship {
  const idx = CELL_REL_CYCLE.indexOf(current);
  const i = idx === -1 ? 0 : idx;
  return CELL_REL_CYCLE[(i + 1) % CELL_REL_CYCLE.length];
}

// ── Status helpers ────────────────────────────────────────────

function statusBorder(status: RoadmapNodeStatus): string {
  switch (status) {
    case 'true': return 'border-l-green-400';
    case 'in-progress': return 'border-l-amber-400';
    case 'not-true': return 'border-l-red-400';
    default: return 'border-l-gray-200 dark:border-l-gray-600';
  }
}

function StatusDot({ status }: { status: RoadmapNodeStatus }) {
  switch (status) {
    case 'true': return <CheckCircle2 className="h-3 w-3 text-[var(--green)] flex-shrink-0" />;
    case 'in-progress': return <Clock className="h-3 w-3 text-[var(--mustard)] flex-shrink-0" />;
    case 'not-true': return <XCircle className="h-3 w-3 text-[var(--coral)] flex-shrink-0" />;
    default: return <Circle className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />;
  }
}

// ── Sortable node card ────────────────────────────────────────

interface NodeCardProps {
  node: RoadmapNode;
  isSelected: boolean;
  onSelect: (id: string) => void;
  isDragOverlay?: boolean;
  isPlaceholder?: boolean;
}

function NodeCard({ node, isSelected, onSelect, isDragOverlay = false, isPlaceholder = false }: NodeCardProps) {
  const { updateNode, deleteNode } = useRoadmapStore();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled: isDragOverlay,
  });

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(node.text);
  const [deleting, setDeleting] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);

  const style = isDragOverlay
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  async function handleSaveEdit() {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== node.text) {
      await updateNode(node.id, { text: trimmed });
    }
    setEditing(false);
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    setDeleting(true);
    await deleteNode(node.id);
  }

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setEditText(node.text);
    setEditing(true);
    setTimeout(() => editRef.current?.focus(), 0);
  }

  if (isPlaceholder || (!isDragOverlay && isDragging)) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-10 mb-1 border-2 border-dashed border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10"
      />
    );
  }

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={style}
      className={`group mb-1  border border-l-4 text-xs transition-shadow
        ${statusBorder(node.status)}
        ${node.status === 'true' ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/15 border-t-green-200 border-r-green-200 border-b-green-200' : ''}
        ${node.status === 'in-progress' ? 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/15 border-t-amber-200 border-r-amber-200 border-b-amber-200' : ''}
        ${node.status === 'not-true' ? 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/15 border-t-red-200 border-r-red-200 border-b-red-200' : ''}
        ${node.status === 'unknown' ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700' : ''}
        ${isSelected ? 'ring-2 ring-purple-500 ring-inset' : ''}
        ${isDragOverlay ? 'shadow-xl opacity-95 rotate-1' : 'hover:shadow-md'}
      `}
    >
      {editing ? (
        <div className="p-2">
          <textarea
            ref={editRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
              if (e.key === 'Escape') { setEditing(false); }
            }}
            className="w-full text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] bg-transparent border border-[var(--slate)] rounded px-1.5 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-purple-400"
            rows={2}
          />
          <div className="flex gap-1 mt-1 justify-end">
            <button onClick={() => setEditing(false)} className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded">
              <X className="h-3 w-3" />
            </button>
            <button onClick={handleSaveEdit} className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] rounded">
              <Check className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-1.5 p-2">
          {!isDragOverlay && (
            <button
              {...attributes}
              {...listeners}
              className="flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors touch-none"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <circle cx="7" cy="5" r="1.3"/><circle cx="13" cy="5" r="1.3"/>
                <circle cx="7" cy="10" r="1.3"/><circle cx="13" cy="10" r="1.3"/>
                <circle cx="7" cy="15" r="1.3"/><circle cx="13" cy="15" r="1.3"/>
              </svg>
            </button>
          )}

          <button
            onClick={() => onSelect(node.id)}
            className="flex-1 min-w-0 text-left flex items-start gap-1.5"
          >
            <StatusDot status={node.status} />
            <PlanNodeWorkspaceItemBadge itemId={node.itemId} />
            <span className={`flex-1 leading-tight text-[var(--text-pri)] dark:text-[var(--text-pri)] ${node.isGoal ? 'font-semibold' : ''}`}>
              {node.text || <span className="italic text-[var(--text-ter)]">Untitled</span>}
            </span>
          </button>

          {!isDragOverlay && (
            <div className="flex-shrink-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5">
              <button
                onClick={startEdit}
                className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] rounded transition-colors"
                title="Edit"
              >
                <Pencil className="h-2.5 w-2.5" />
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-0.5 text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] rounded transition-colors"
                title="Delete"
              >
                {deleting ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Trash2 className="h-2.5 w-2.5" />}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Cell relationship connector ───────────────────────────────

function ConnectorCaptionInput({ node }: { node: RoadmapNode }) {
  const { updateNode } = useRoadmapStore();
  const [local, setLocal] = useState(node.cellConnectorCaption ?? '');

  useEffect(() => {
    setLocal(node.cellConnectorCaption ?? '');
  }, [node.cellConnectorCaption, node.id]);

  return (
    <input
      type="text"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => updateNode(node.id, { cellConnectorCaption: local.trim() || null })}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      placeholder="Optional label (cosmetic only)"
      title="Stored on this row; planning logic ignores this label."
      className="w-full rounded border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-neutral-950/40 px-1.5 py-0.5 text-[9px] text-[var(--text-sec)] placeholder:text-[var(--text-ter)]"
    />
  );
}

function CellConnector({ node }: { node: RoadmapNode }) {
  const { updateNode } = useRoadmapStore();
  const rel = node.cellRelationship;

  function cycle(e: React.MouseEvent) {
    e.stopPropagation();
    updateNode(node.id, { cellRelationship: advanceCellRelationship(rel) });
  }

  const cycleHint =
    'Click to cycle: depends on (↑) → both required (+ and) → and then → cosmetic divider';

  switch (rel) {
    case 'sequential':
      return (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={cycle}
            title={`Depends on — the item below needs the item above resolved first. ${cycleHint}`}
            className="w-full flex items-center gap-1.5 px-2 py-0.5 my-0.5 rounded text-[10px] font-medium transition-colors bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 text-[var(--mustard)] dark:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:hover:bg-[var(--mustard)]/40"
          >
            <Link2 className="h-3 w-3 flex-shrink-0" />
            depends on ↑
          </button>
        </div>
      );
    case 'parallel':
      return (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={cycle}
            title={`Both items are required toward the milestone; sequencing is unspecified. ${cycleHint}`}
            className="w-full flex items-center gap-1.5 px-2 py-0.5 my-0.5 rounded text-[10px] font-medium transition-colors bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)]"
          >
            <GitMerge className="h-3 w-3 flex-shrink-0" />
            + and
          </button>
        </div>
      );
    case 'then':
      return (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={cycle}
            title={`Narrative "and then": useful for reading order — not the same tool as directed dependencies. ${cycleHint}`}
            className="w-full flex items-center gap-1.5 px-2 py-0.5 my-0.5 rounded text-[10px] font-medium transition-colors bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/45"
          >
            <ArrowDownToLine className="h-3 w-3 flex-shrink-0" />
            and then
          </button>
        </div>
      );
    case 'divider':
      return (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={cycle}
            title={`Visual separator only — no planning meaning in the roadmap model. ${cycleHint}`}
            className="w-full rounded text-[10px] font-medium transition-colors my-0.5 border border-dashed border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)]/50 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--bg)]"
          >
            <span className="flex items-center justify-center gap-1.5 px-2 py-1">
              <GripHorizontal className="h-3 w-3 flex-shrink-0" />
              Divider
            </span>
          </button>
          <ConnectorCaptionInput node={node} />
        </div>
      );
    default:
      return (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={cycle}
            title={cycleHint}
            className="w-full px-2 py-0.5 my-0.5 rounded border border-[var(--rule)] text-[10px] text-[var(--text-sec)]"
          >
            Set connector…
          </button>
        </div>
      );
  }
}

// ── Droppable + sortable cell ─────────────────────────────────

interface CellProps {
  cellId: string;
  laneId: string | null;
  milestoneId: string | null;
  nodes: RoadmapNode[];
  activeId: string | null;
  selectedNodeId: string | null;
  onSelect: (id: string) => void;
  onAddNode: (laneId: string | null, milestoneId: string | null, text?: string) => Promise<void>;
  isAddingTo: string | null;
}

function Cell({
  cellId,
  laneId,
  milestoneId,
  nodes,
  activeId: _activeId,
  selectedNodeId,
  onSelect,
  onAddNode,
  isAddingTo,
}: CellProps) {
  const { setNodeRef, isOver } = useDroppable({ id: cellId });
  const isAdding = isAddingTo === cellId;
  const [showInput, setShowInput] = useState(false);
  const [inputText, setInputText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const visualNodes = graphVisible(nodes);

  function openInput() {
    setInputText('');
    setShowInput(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function handleCommit() {
    const text = inputText.trim();
    if (!text) { setShowInput(false); return; }
    await onAddNode(laneId, milestoneId, text);
    setInputText('');
    setShowInput(false);
  }

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[72px] p-2  transition-colors relative group/cell
        ${isOver ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 ring-2 ring-purple-300 dark:ring-purple-700 ring-inset' : ''}
      `}
    >
      <SortableContext items={visualNodes.map((n) => n.id)} strategy={verticalListSortingStrategy}>
        {visualNodes.map((n, i) => (
          <div key={n.id}>
            {i > 0 && <CellConnector node={n} />}
            <NodeCard
              node={n}
              isSelected={n.id === selectedNodeId}
              onSelect={onSelect}
            />
          </div>
        ))}
      </SortableContext>

      {visualNodes.length > 1 && (
        <div className="flex justify-end mt-1 pr-0.5">
          <span className="text-[9px] text-[var(--text-ter)] italic select-none">
            all required →
          </span>
        </div>
      )}

      {showInput ? (
        <div className="mt-1 flex items-center gap-1">
          <input
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleCommit(); }
              if (e.key === 'Escape') { setShowInput(false); }
            }}
            onBlur={() => { if (!inputText.trim()) setShowInput(false); }}
            placeholder="Condition text…"
            className="flex-1 min-w-0 text-[11px] px-1.5 py-1 rounded border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-1 focus:ring-purple-400"
          />
          {isAdding
            ? <Loader2 className="h-3 w-3 animate-spin text-[var(--text-ter)] flex-shrink-0" />
            : (
              <button
                onMouseDown={(e) => { e.preventDefault(); handleCommit(); }}
                className="flex-shrink-0 p-0.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] rounded"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            )
          }
        </div>
      ) : (
        <button
          onClick={openInput}
          className={`flex items-center justify-center gap-1 w-full mt-1 py-1 rounded text-[10px] font-medium transition-colors
            ${visualNodes.length === 0
              ? 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 border border-dashed border-[var(--rule)] hover:border-[var(--slate)]'
              : 'text-[var(--text-ter)] opacity-0 group-hover/cell:opacity-100 hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20'
            }`}
          title="Add condition to this cell"
        >
          <Plus className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ── Zoom controls overlay ─────────────────────────────────────

function ZoomControls({
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center gap-1 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] shadow-sm p-1">
      <button
        onClick={onZoomOut}
        disabled={zoom <= 0.4}
        className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] disabled:opacity-40 transition-colors"
        title="Zoom out"
      >
        <ZoomOut className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={onReset}
        className="px-2 py-1 text-[11px] font-mono text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] rounded transition-colors min-w-[42px] text-center"
        title="Reset zoom"
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        onClick={onZoomIn}
        disabled={zoom >= 1.5}
        className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] disabled:opacity-40 transition-colors"
        title="Zoom in"
      >
        <ZoomIn className="h-3.5 w-3.5" />
      </button>
      <div className="w-px h-4 bg-[var(--hover-tint)] mx-0.5" />
      <button
        onClick={onReset}
        className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] transition-colors"
        title="Fit to view"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────

const STATUS_LEGEND = [
  {
    status: 'in-progress' as RoadmapNodeStatus,
    label: 'In Progress',
    sublabel: 'Committed — actively in flight',
    bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/15',
    border: 'border-l-amber-400',
    icon: <Clock className="h-3 w-3 text-[var(--mustard)]" />,
    dot: 'bg-[var(--mustard)]',
  },
  {
    status: 'unknown' as RoadmapNodeStatus,
    label: 'Not Started',
    sublabel: 'Likely / Aspirational — planned',
    bg: 'bg-[var(--bg-raised)] dark:bg-[var(--surface)]',
    border: 'border-l-gray-300 dark:border-l-gray-600',
    icon: <Circle className="h-3 w-3 text-[var(--text-ter)]" />,
    dot: 'bg-[var(--rule-bright)]',
  },
  {
    status: 'true' as RoadmapNodeStatus,
    label: 'Done',
    sublabel: 'Condition confirmed true',
    bg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/15',
    border: 'border-l-green-400',
    icon: <CheckCircle2 className="h-3 w-3 text-[var(--green)]" />,
    dot: 'bg-[var(--green)]',
  },
  {
    status: 'not-true' as RoadmapNodeStatus,
    label: 'Not True',
    sublabel: 'Condition blocked or invalidated',
    bg: 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/15',
    border: 'border-l-red-400',
    icon: <XCircle className="h-3 w-3 text-[var(--coral)]" />,
    dot: 'bg-[var(--coral)]',
  },
] as const;

function Legend({
  active,
  onToggle,
}: {
  active: Set<RoadmapNodeStatus>;
  onToggle: (s: RoadmapNodeStatus) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5  border text-xs font-medium transition-colors
          ${open
            ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-[var(--slate)] dark:border-[var(--slate)] text-[var(--text-sec)]'
            : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
          }`}
        title="Show legend and filter by status"
      >
        <Info className="h-3.5 w-3.5" />
        Legend
        {active.size < STATUS_LEGEND.length && (
          <span className="ml-0.5 px-1 py-0 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-[10px] font-semibold">
            {active.size}/{STATUS_LEGEND.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-72 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] shadow-lg z-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
              Card Status
            </span>
            <button
              onClick={() => setOpen(false)}
              className="p-0.5 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)]"
            >
              <X className="h-3 w-3" />
            </button>
          </div>

          <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3 leading-snug">
            Click a row to show or hide cards with that status. Change a card's status by clicking it and using the detail panel.
          </p>

          <div className="space-y-1.5">
            {STATUS_LEGEND.map((entry) => {
              const isActive = active.has(entry.status);
              return (
                <button
                  key={entry.status}
                  onClick={() => onToggle(entry.status)}
                  className={`w-full flex items-center gap-2.5 p-2  border-l-4 transition-all text-left
                    ${entry.bg} ${entry.border}
                    ${isActive ? 'opacity-100' : 'opacity-40 grayscale'}
                    hover:opacity-100 hover:grayscale-0`}
                >
                  {entry.icon}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {entry.label}
                    </div>
                    <div className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate">
                      {entry.sublabel}
                    </div>
                  </div>
                  {isActive
                    ? <Check className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                    : <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  }
                </button>
              );
            })}
          </div>

          <div className="mt-3 pt-3 border-t border-[var(--rule)] flex gap-2">
            <button
              onClick={() => STATUS_LEGEND.forEach((e) => { if (!active.has(e.status)) onToggle(e.status); })}
              className="flex-1 text-[11px] text-center py-1 bg-[var(--surface)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] transition-colors"
            >
              Show all
            </button>
            <button
              onClick={() => STATUS_LEGEND.forEach((e) => { if (active.has(e.status)) onToggle(e.status); })}
              className="flex-1 text-[11px] text-center py-1 bg-[var(--surface)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] transition-colors"
            >
              Hide all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface GoalData {
  title: string;
  objective?: string;
  futurePoint?: string;
  definedSuccess?: string | null;
}

function GoalPanel({ goal }: { goal: GoalData }) {
  return (
    <div
      className="flex-shrink-0 flex flex-col justify-center relative overflow-hidden w-[160px] max-md:w-full"
      style={{
        clipPath: 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)',
        background: 'linear-gradient(160deg, #7c3aed 0%, #4f46e5 100%)',
        minHeight: 80,
      }}
    >
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.4) 0%, transparent 60%)',
        }}
      />

      <div className="relative flex flex-col gap-1.5 px-4 py-3 pl-6">
        <div className="flex items-center gap-1">
          <Trophy className="h-3 w-3 text-[var(--mustard)] flex-shrink-0" />
          <span className="text-[9px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">
            Goal State
          </span>
        </div>

        <p className="text-xs font-semibold text-white leading-snug break-words">
          {goal.title || <span className="italic opacity-60">Untitled goal</span>}
        </p>

        {goal.objective && goal.objective !== goal.title && (
          <p className="text-[10px] text-[var(--text-ter)] leading-snug break-words">
            {goal.objective}
          </p>
        )}

        <div className="flex flex-col gap-0.5 mt-0.5">
          {goal.futurePoint && (
            <div className="flex items-center gap-1 text-[9px] text-[var(--text-ter)]">
              <Calendar className="h-2 w-2 flex-shrink-0" />
              <span>{goal.futurePoint}</span>
            </div>
          )}
          {goal.definedSuccess && (
            <div className="flex items-start gap-1 text-[9px] text-[var(--text-ter)]">
              <Target className="h-2 w-2 flex-shrink-0 mt-0.5" />
              <span className="break-words">{goal.definedSuccess}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Arrow chevron cell (last column in each row) ──────────────

function ArrowChevronCell({ rowSpan = 1 }: { rowSpan?: number }) {
  return (
    <td
      rowSpan={rowSpan}
      className="border-b border-[var(--rule)] align-middle bg-[var(--surface)] dark:bg-[var(--bg-raised)]"
      style={{ width: 22, minWidth: 22, padding: 0 }}
    >
      <div className="flex items-center justify-center h-full py-3">
        <svg
          className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </div>
    </td>
  );
}

// ── Main GridView ─────────────────────────────────────────────

interface GridViewProps {
  roadmapId: string;
  nodes: RoadmapNode[];
  swimlanes: RoadmapSwimlane[];
  milestones: RoadmapMilestoneColumn[];
  onNodeSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  goal?: GoalData;
}

export function GridView({
  roadmapId,
  nodes,
  swimlanes,
  milestones,
  onNodeSelect,
  selectedNodeId,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  goal,
}: GridViewProps) {
  const { updateNode, createNode } = useRoadmapStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [activeStatuses, setActiveStatuses] = useState<Set<RoadmapNodeStatus>>(
    () => new Set<RoadmapNodeStatus>(['unknown', 'in-progress', 'true', 'not-true'])
  );
  const [expandedSwimlaneId, setExpandedSwimlaneId] = useState<string | null>(null);
  const { isPhone } = useVisualViewportFlags();

  const toggleStatus = (s: RoadmapNodeStatus) =>
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { next.delete(s); } else { next.add(s); }
      return next;
    });

  const visibleNodes = nodes.filter((n) => activeStatuses.has(n.status));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const toCellId = (laneId: string | null, milestoneId: string | null) =>
    `cell::${laneId ?? 'none'}::${milestoneId ?? 'none'}`;

  const fromCellId = (cellId: string): { laneId: string | null; milestoneId: string | null } => {
    const without = cellId.slice('cell::'.length);
    const sep = without.indexOf('::');
    if (sep === -1) return { laneId: null, milestoneId: null };
    const rawLane = without.slice(0, sep);
    const rawMilestone = without.slice(sep + 2);
    return {
      laneId: rawLane === 'none' ? null : rawLane,
      milestoneId: rawMilestone === 'none' ? null : rawMilestone,
    };
  };

  const nodesInCell = useCallback(
    (laneId: string | null, milestoneId: string | null) =>
      visibleNodes
        .filter((n) => n.laneId === laneId && n.milestoneId === milestoneId)
        .sort((a, b) => a.position - b.position),
    [visibleNodes]
  );

  const cellOfNode = useCallback(
    (nodeId: string): string | null => {
      const n = nodes.find((n) => n.id === nodeId);
      if (!n) return null;
      return toCellId(n.laneId, n.milestoneId);
    },
    [nodes]
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeNodeId = String(active.id);
      const overId = String(over.id);

      let destCellId: string;
      if (overId.startsWith('cell::')) {
        destCellId = overId;
      } else {
        const destCell = cellOfNode(overId);
        if (!destCell) return;
        destCellId = destCell;
      }

      const srcCellId = cellOfNode(activeNodeId);
      if (!srcCellId || srcCellId === destCellId) return;

      const { laneId, milestoneId } = fromCellId(destCellId);
      const destNodes = nodesInCell(laneId, milestoneId);
      const maxPos = destNodes.reduce((m, n) => Math.max(m, n.position), -1);
      updateNode(activeNodeId, { laneId, milestoneId, position: maxPos + 1 });
    },
    [cellOfNode, nodesInCell, updateNode]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over) return;

      const activeNodeId = String(active.id);
      const overId = String(over.id);

      if (overId.startsWith('cell::')) {
        return;
      }

      const activeNode = nodes.find((n) => n.id === activeNodeId);
      const overNode = nodes.find((n) => n.id === overId);
      if (!activeNode || !overNode) return;

      if (
        activeNode.laneId === overNode.laneId &&
        activeNode.milestoneId === overNode.milestoneId
      ) {
        const cell = nodesInCell(activeNode.laneId, activeNode.milestoneId);
        const oldIdx = cell.findIndex((n) => n.id === activeNodeId);
        const newIdx = cell.findIndex((n) => n.id === overId);
        if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
        const reordered = arrayMove(cell, oldIdx, newIdx);
        await Promise.all(
          reordered.map((n, i) =>
            n.position !== i ? updateNode(n.id, { position: i }) : Promise.resolve({ success: true })
          )
        );
      }
    },
    [nodes, nodesInCell, updateNode]
  );

  const handleAddToCell = useCallback(
    async (laneId: string | null, milestoneId: string | null, text = '') => {
      const cellId = toCellId(laneId, milestoneId);
      setAddingTo(cellId);
      const cellNodes = nodesInCell(laneId, milestoneId);
      const maxPos = cellNodes.reduce((m, n) => Math.max(m, n.position), -1);
      const { node } = await createNode(roadmapId, {
        text: text.trim() || 'New condition',
        tense: 'future',
        isGoal: false,
        laneId,
        milestoneId,
        position: maxPos + 1,
      });
      setAddingTo(null);
      if (node) onNodeSelect(node.id);
    },
    [roadmapId, createNode, nodesInCell, onNodeSelect]
  );

  const activeNode = activeId ? nodes.find((n) => n.id === activeId) ?? null : null;

  const hasLayout = swimlanes.length > 0 || milestones.length > 0;
  const unassigned = graphVisible(
    nodes.filter((n) => !n.laneId && !n.milestoneId).sort((a, b) => a.position - b.position),
  );

  const COL_WIDTH = 200;

  const toggleSwimlane = (laneId: string) => {
    setExpandedSwimlaneId((prev) => (prev === laneId ? null : laneId));
  };

  const renderCell = (
    laneId: string | null,
    milestone: RoadmapMilestoneColumn,
  ) => (
    <Cell
      key={milestone.id}
      cellId={toCellId(laneId, milestone.id)}
      laneId={laneId}
      milestoneId={milestone.id}
      nodes={nodesInCell(laneId, milestone.id)}
      activeId={activeId}
      selectedNodeId={selectedNodeId}
      onSelect={onNodeSelect}
      onAddNode={handleAddToCell}
      isAddingTo={addingTo}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {!hasLayout && (
        <div className="mb-4 p-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)] text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          Grid view works best with swimlanes and milestone columns. Use the <strong>Layout</strong> button to add them.
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2 flex-wrap">
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Drag nodes between cells · Click <strong>+</strong> in any cell to add a condition
        </p>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Legend active={activeStatuses} onToggle={toggleStatus} />
          {!isPhone && (
            <ZoomControls zoom={zoom} onZoomIn={onZoomIn} onZoomOut={onZoomOut} onReset={onZoomReset} />
          )}
        </div>
      </div>

      {/* On a phone the goal state goes under the lanes, not in a 160px column beside them */}
      <div className="flex items-stretch gap-0 max-md:flex-col max-md:gap-3">

        {isPhone && swimlanes.length > 0 ? (
          <div className="flex-1 min-w-0 border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {swimlanes.map((lane) => {
              const laneNodeCount = milestones.reduce(
                (sum, m) => sum + nodesInCell(lane.id, m.id).length,
                0,
              );
              const isOpen = expandedSwimlaneId === lane.id;

              return (
                <div key={lane.id}>
                  <button
                    type="button"
                    onClick={() => toggleSwimlane(lane.id)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-[var(--surface)] dark:bg-[var(--bg-raised)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                      {lane.name}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        {laneNodeCount} node{laneNodeCount !== 1 ? 's' : ''}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                      {milestones.map((m) => (
                        <div key={m.id} className="px-3 py-2">
                          <div className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
                            {m.name}
                            {m.condition && (
                              <span className="block text-[10px] font-normal text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                                {m.condition}
                              </span>
                            )}
                          </div>
                          {renderCell(lane.id, m)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
        <div
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-auto border border-[var(--rule)]"
          style={{ maxHeight: 'calc(100vh - 280px)' }}
        >
          <div
            style={{
              transform: isPhone ? undefined : `scale(${zoom})`,
              transformOrigin: 'top left',
              width: isPhone ? undefined : zoom < 1 ? `${100 / zoom}%` : undefined,
              minWidth: isPhone ? undefined : zoom >= 1 ? `${(milestones.length + 2) * COL_WIDTH}px` : undefined,
            }}
          >
            {isPhone && milestones.length > 0 && swimlanes.length === 0 ? (
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {milestones.map((m) => (
                  <div key={m.id} className="px-3 py-3">
                    <div className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
                      {m.name}
                    </div>
                    {renderCell(null, m)}
                  </div>
                ))}
              </div>
            ) : (
            <table className="border-collapse text-sm" style={{ width: '100%' }}>
              {milestones.length > 0 && (
                <thead>
                  <tr>
                    {swimlanes.length > 0 && (
                      <th
                        className="p-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] border-b border-r border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] sticky left-0 top-0 z-30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]"
                        style={{ minWidth: 130 }}
                      />
                    )}
                    {milestones.map((m) => (
                      <th
                        key={m.id}
                        className="p-3 text-center text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] border-b border-r border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] sticky top-0 z-20"
                        style={{ minWidth: COL_WIDTH }}
                      >
                        <div>{m.name}</div>
                        {m.condition && (
                          <div className="text-[10px] font-normal text-[var(--text-ter)] mt-0.5">{m.condition}</div>
                        )}
                      </th>
                    ))}
                    <th
                      className="sticky top-0 z-20 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]"
                      style={{ width: 28, minWidth: 28 }}
                    />
                  </tr>
                </thead>
              )}

              <tbody>
                {swimlanes.length > 0 ? (
                  swimlanes.map((lane) => (
                    <tr key={lane.id}>
                      <td
                        className="p-3 text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] border-r border-b border-[var(--rule)] align-top bg-[var(--surface)] dark:bg-[var(--bg-raised)] sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]"
                        style={{ minWidth: 130 }}
                      >
                        {lane.name}
                      </td>
                      {milestones.map((m) => (
                        <td
                          key={m.id}
                          className="border-r border-b border-[var(--rule)] align-top"
                          style={{ minWidth: COL_WIDTH }}
                        >
                          {renderCell(lane.id, m)}
                        </td>
                      ))}
                      <ArrowChevronCell />
                    </tr>
                  ))
                ) : (
                  <tr>
                    {milestones.map((m) => (
                      <td
                        key={m.id}
                        className="border-r border-b border-[var(--rule)] align-top"
                        style={{ minWidth: COL_WIDTH }}
                      >
                        {renderCell(null, m)}
                      </td>
                    ))}
                    <ArrowChevronCell />
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
        )}

        {goal && <GoalPanel goal={goal} />}
      </div>

      {hasLayout && unassigned.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--text-ter)]" />
            Unassigned nodes
          </h4>
          <div className="flex flex-wrap gap-2 p-3 border border-dashed border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg-raised)]/20">
            {unassigned.map((n) => (
              <div key={n.id} className="w-[180px]">
                <NodeCard
                  node={n}
                  isSelected={n.id === selectedNodeId}
                  onSelect={onNodeSelect}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <DragOverlay dropAnimation={null}>
        {activeNode ? (
          <div style={{ width: COL_WIDTH - 16 }}>
            <NodeCard
              node={activeNode}
              isSelected={false}
              onSelect={() => {}}
              isDragOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
